import { CartUpdateEvent } from '@theme/events';

/**
 * Third-party cart bridge.
 *
 * Some apps (Sectionly product options, Timesact pre-order) intercept the
 * add-to-cart click and POST to /cart/add with their own fetch call, bypassing
 * the theme's product-form-component. The theme's `cart:update` event is then
 * never dispatched, so the cart badge, drawer contents and drawer auto-open
 * all silently break while the item still reaches the cart.
 *
 * This module watches for those third-party add requests and re-emits the
 * theme event after they succeed. Listeners handle the rest:
 * - cart-icon updates the badge from `itemCount`
 * - cart-drawer-component auto-opens when the `auto-open` setting is on
 * - cart-items-component re-renders itself when no `sections` are provided
 *
 * Detection: the theme always submits FormData; the observed apps send a JSON
 * string body (`{"items":[...]}`).
 */

const CART_ADD_URL = /\/cart\/add(?:\.js)?(?:\?.*)?$/;

/** @type {Promise<void> | null} */
let pendingSync = null;

/**
 * @param {string | URL | Request} input
 * @param {RequestInit} [init]
 * @returns {boolean}
 */
function isThirdPartyCartAdd(input, init) {
  const url = input instanceof Request ? input.url : String(input);
  if (!CART_ADD_URL.test(url)) return false;

  if (init?.body != null) return typeof init.body === 'string';

  if (input instanceof Request) {
    return (input.headers.get('content-type') || '').includes('json');
  }

  return false;
}

/**
 * Fetches the live cart and re-emits the theme cart event so badge, drawer
 * and listeners resync after a third-party add.
 * @returns {Promise<void>}
 */
function syncCartAfterExternalAdd() {
  if (pendingSync) return pendingSync;

  pendingSync = (async () => {
    try {
      const response = await window.fetch('/cart.js');
      if (!response.ok) return;
      const cart = await response.json();

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'cart-bridge', {
          source: 'cart-bridge',
          itemCount: cart.item_count,
        })
      );
    } catch {
      // Best-effort UI sync; the item is already added server-side.
    } finally {
      pendingSync = null;
    }
  })();

  return pendingSync;
}

const originalFetch = window.fetch.bind(window);

window.fetch = /** @type {typeof window.fetch} */ (
  function (input, init) {
    const thirdPartyAdd = isThirdPartyCartAdd(input, init);
    const request = originalFetch(input, init);

    if (thirdPartyAdd) {
      request
        .then((response) => {
          if (response.ok) syncCartAfterExternalAdd();
        })
        .catch(() => {
          // The app's own request failed; nothing to sync.
        });
    }

    return request;
  }
);
